import React, { useRef, useState } from 'react';
import {
    Box,
    CircularProgress
} from '@material-ui/core';
import { notifyConfirm } from '../../util';

export const SWIPE_ANIMATIONS = {
    remove: "remove",
    loading: "loading",
    none: "none",
};

export const SwipeableRow = ({ children, action = null, secondaryAction = null }) => {
    // State to control the horizontal movement of the swiped element
    const [translateX, setTranslateX] = useState(0);
    // State to manage current animation phase (e.g., waiting for API, removing row)
    const [animation, setAnimation] = useState(SWIPE_ANIMATIONS.none);

    // Refs are used here instead of state to avoid unnecessary re-renders during rapid pointer movements
    const startX = useRef(0);
    const currentX = useRef(0);
    const deltaX = useRef(0);
    const dragging = useRef(false);

    // Minimum distance (in pixels) required to trigger the swipe action
    const threshold = 80;

    /**
     * Handles the confirmation flow and triggers the appropriate animation/callback.
     * @param {string} type - The type of animation to run.
     * @param {object} currentAction - The action being triggered (primary or secondary).
     * @param {function} callback - The function to execute once confirmed.
     */
    const runAnimation = async (type, currentAction, callback) => {
        // Check if the action requires user confirmation before proceeding
        if (currentAction?.confirmation?.confirm) {
            const confirmed = await notifyConfirm(currentAction.confirmation.message);
            console.log(confirmed);
            
            // Abort action and reset position if the user cancels
            if (!confirmed.isConfirmed) {
                setTranslateX(0); 
                return;
            }
        }
        
        setAnimation(type);

        // Delay the callback slightly for the "remove" animation to let the CSS transition finish
        if (type === SWIPE_ANIMATIONS.remove) {
            setTimeout(() => {
                callback?.();
            }, 300);
        }

        // Execute immediately for loading, as the UI just shows a spinner
        if (type === SWIPE_ANIMATIONS.loading) {
            callback?.();
        }
    };

    // Triggered when the user first touches or clicks the row
    const handlePointerDown = (e) => {
        const canSwipeLeft = action && action.swipeEnabled;
        const canSwipeRight = secondaryAction && secondaryAction.swipeEnabled;
        
        // Ignore if no swipe actions are configured or if currently loading
        if (!canSwipeLeft && !canSwipeRight) return;
        if (animation === SWIPE_ANIMATIONS.loading) return;

        dragging.current = true;
        startX.current = e.clientX;
        deltaX.current = 0;
    };

    // Triggered continuously as the user drags their pointer
    const handlePointerMove = (e) => {
        if (!dragging.current) return;
        if (animation === SWIPE_ANIMATIONS.loading) return;

        currentX.current = e.clientX;
        const delta = currentX.current - startX.current;
        deltaX.current = delta;

        // Swiping Left (Negative delta): Reveals the Primary Action on the right side
        if (delta < 0 && action?.swipeEnabled) {
            setTranslateX(Math.max(delta, -120)); // Cap the movement at -120px
        } 
        // Swiping Right (Positive delta): Reveals the Secondary Action on the left side
        else if (delta > 0 && secondaryAction?.swipeEnabled) {
            setTranslateX(Math.min(delta, 120)); // Cap the movement at +120px
        }
    };

    // Triggered when the user releases the touch/click or leaves the element
    const handlePointerUp = () => {
        if (animation === SWIPE_ANIMATIONS.loading) return;

        const delta = deltaX.current || 0;

        // Check if user swiped far enough left to trigger the Primary Action
        if (delta < -threshold && action?.swipeEnabled) {
            runAnimation(
                action.animation || SWIPE_ANIMATIONS.remove,
                action,
                action.onSwipe
            );
        } 
        // Check if user swiped far enough right to trigger the Secondary Action
        else if (delta > threshold && secondaryAction?.swipeEnabled) {
            runAnimation(
                secondaryAction.animation || SWIPE_ANIMATIONS.remove,
                secondaryAction,
                secondaryAction.onSwipe
            );
        } else {
            // The threshold wasn't reached, snap the row back to its original position
            setTranslateX(0);
        }

        // Reset drag state variables for the next interaction
        dragging.current = false;
        startX.current = 0;
        currentX.current = 0;
        deltaX.current = 0;
    };

    const isRemoving = animation === SWIPE_ANIMATIONS.remove;

    return (
        <Box
            position="relative"
            overflow="hidden"
            // Container styles handle the collapse animation when the row is successfully "removed"
            style={{
                maxHeight: isRemoving ? 0 : 200,
                opacity: isRemoving ? 0 : 1,
                transform: isRemoving ? 'translateY(-8px)' : 'translateY(0)',
                transition:
                    'max-height 0.35s ease, opacity 0.25s ease, transform 0.25s ease, margin 0.3s ease',
                marginBottom: isRemoving ? 0 : 4,
            }}
        >
            {/* PRIMARY ACTION BACKGROUND (Positioned on the Right, revealed by swiping left) */}
            <Box
                position="absolute"
                right={0}
                top={0}
                bottom={0}
                width={120}
                display="flex"
                alignItems="center"
                justifyContent="space-around"
            >
                {action && action.swipeEnabled && (
                    <Box>
                        {action.icon}
                    </Box>
                )}
            </Box>

            {/* SECONDARY ACTION BACKGROUND (Positioned on the Left, revealed by swiping right) */}
            <Box
                position="absolute"
                left={0}
                top={0}
                bottom={0}
                width={120}
                display="flex"
                alignItems="center"
                justifyContent="space-around"
            >
                {secondaryAction && secondaryAction.swipeEnabled && (
                    <Box>
                        {secondaryAction.icon}
                    </Box>
                )}
            </Box>

            {/* LOADING OVERLAY (Blocks interaction and shows spinner while processing) */}
            {animation === SWIPE_ANIMATIONS.loading && (
                <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bgcolor="rgba(255,255,255,0.6)"
                    zIndex={10}
                >
                    <CircularProgress size={24} />
                </Box>
            )}

            {/* MAIN DRAGGABLE CONTENT */}
            <Box
                position="relative"
                style={{
                    transform: `translateX(${translateX}px)`,
                    // Remove transition during active dragging so it sticks to the cursor/finger,
                    // otherwise animate smoothly back to 0 or out of view
                    transition: dragging.current ? 'none' : '0.25s ease',
                    touchAction: 'pan-y', // Allow vertical scrolling on touch devices without triggering swipe
                    zIndex: 5, // Ensure the content sits above the action backgrounds
                }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerLeave={handlePointerUp}
            >
                {children}
            </Box>
        </Box>
    );
};